import { Link } from "react-router-dom";
import { Badge } from "@mbbe/ui";
import {
  Check,
  ClipboardList,
  CreditCard,
  FileText,
  MessageSquare,
  ShieldAlert,
  Ticket,
  Wrench,
} from "lucide-react";
import type { CustomerNotification, SupportIssueStatus } from "@mbbe/types";
import { STATUS_COLORS, formatDateTime } from "../lib/labels";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  localizedNotificationCopy,
  notificationPortalHref,
  notificationPresentation,
} from "../../portal/notifications/presentation";

interface NotificationItemProps {
  notification: CustomerNotification;
  onMarkRead: (id: string) => Promise<unknown>;
  onOpenIssue: () => void;
  resolveIssueHref?: (issueId: string, linkPath?: string | null) => string;
}

export type NotificationLifecycle =
  "action-required" | "team-reply" | "completed" | "rejected-error";

type NotificationStatusSource = CustomerNotification & {
  status?: SupportIssueStatus | null;
};

export function getNotificationStatus(
  notification: CustomerNotification,
): SupportIssueStatus | null {
  const source = notification as NotificationStatusSource;
  return (
    notification.issueStatus ||
    source.status ||
    notification.data?.status ||
    notification.data?.nextStatus ||
    null
  );
}

export function isActionRequiredNotification(
  notification: CustomerNotification,
): boolean {
  const status = getNotificationStatus(notification);
  return (
    notificationPresentation(notification.type).actionRequired ||
    status === "waiting_customer"
  );
}

export function getNotificationLifecycle(
  notification: CustomerNotification,
): NotificationLifecycle {
  const status = getNotificationStatus(notification);
  const presentation = notificationPresentation(notification.type);
  if (isActionRequiredNotification(notification)) return "action-required";
  if (status === "rejected") return "rejected-error";
  if (status === "resolved" || status === "closed") return "completed";
  return presentation.lifecycle;
}

function NotificationIcon({ type }: { type: string }) {
  if (type.startsWith("client_task_"))
    return (
      <ClipboardList data-testid="client-task-notification-icon" size={15} />
    );
  if (type.startsWith("payment_"))
    return <CreditCard data-testid="payment-notification-icon" size={15} />;
  if (type.startsWith("progress_report_"))
    return <FileText data-testid="report-notification-icon" size={15} />;
  if (type === "support_issue_comment") return <MessageSquare size={15} />;
  if (type === "support_issue_reopen_update") return <Wrench size={15} />;
  if (type === "support_issue_resolved") return <Check size={15} />;
  return <Ticket size={15} />;
}

function formatNotificationTime(iso: string, language: "tr" | "en"): string {
  const created = new Date(iso);
  const timestamp = created.getTime();
  if (Number.isNaN(timestamp)) return formatDateTime(iso, language);

  const diffMs = timestamp - Date.now();
  const absMs = Math.abs(diffMs);
  const formatter = new Intl.RelativeTimeFormat(
    language === "tr" ? "tr-TR" : "en-US",
    { numeric: "auto" },
  );

  if (absMs < 60_000) return language === "tr" ? "az önce" : "just now";
  if (absMs < 60 * 60_000)
    return formatter.format(Math.round(diffMs / 60_000), "minute");
  if (absMs < 24 * 60 * 60_000)
    return formatter.format(Math.round(diffMs / (60 * 60_000)), "hour");
  if (absMs < 7 * 24 * 60 * 60_000)
    return formatter.format(Math.round(diffMs / (24 * 60 * 60_000)), "day");
  return formatDateTime(iso, language);
}

export function NotificationItem({
  notification,
  onMarkRead,
  onOpenIssue,
  resolveIssueHref,
}: NotificationItemProps) {
  const { t, language } = useLanguage();
  const localized = localizedNotificationCopy(notification, language);
  const isUnread = !notification.readAt;
  const status = getNotificationStatus(notification);
  const issueTitle = notification.issueTitle || notification.data?.issueTitle;
  const projectName =
    notification.projectName || notification.data?.projectName;
  const isActionRequired = isActionRequiredNotification(notification);
  const isRejected =
    getNotificationLifecycle(notification) === "rejected-error";
  const fullDate = formatDateTime(notification.createdAt, language);
  const href =
    notification.entityType === "support_issue" && resolveIssueHref
      ? resolveIssueHref(notification.entityId, notification.linkPath)
      : notificationPortalHref(notification, "/support");
  const content = (
    <>
      <span
        aria-hidden="true"
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${
          isRejected
            ? "border-red-400/30 bg-red-500/10 text-red-200"
            : isUnread
              ? "border-brand-400/40 bg-brand-500/15 text-brand-100 shadow-sm shadow-brand-500/20"
              : "border-white/5 bg-slate-900/80 text-slate-500"
        }`}
      >
        {isRejected ? (
          <ShieldAlert size={15} />
        ) : (
          <NotificationIcon type={notification.type} />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-start justify-between gap-2">
          <span
            className={`line-clamp-2 text-sm ${isUnread ? "font-semibold text-white" : "font-medium text-slate-200"}`}
          >
            {localized.title}
          </span>
          <span className="flex shrink-0 flex-wrap justify-end gap-1">
            {isActionRequired && (
              <Badge
                variant="warning"
                size="sm"
                className="rounded-md px-1.5 text-[10px] uppercase tracking-[0.08em]"
              >
                {t("destek_action")}
              </Badge>
            )}
            {isUnread && (
              <Badge
                variant="brand"
                size="sm"
                className="rounded-md px-1.5 text-[10px]"
              >
                {t("destek_filter_unread")}
              </Badge>
            )}
          </span>
        </span>
        {(issueTitle || status || projectName) && (
          <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] leading-5 text-slate-400">
            {issueTitle && (
              <span className="max-w-full truncate font-medium text-slate-200">
                {issueTitle}
              </span>
            )}
            {status && (
              <Badge
                variant={STATUS_COLORS[status]}
                size="sm"
                className="rounded-md px-1.5 py-0 text-[10px]"
              >
                {t("status_" + status)}
              </Badge>
            )}
            {projectName && (
              <span className="truncate text-slate-500">
                {t("new_input_project").replace("*", "").trim()}: {projectName}
              </span>
            )}
          </span>
        )}
        <span className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-400">
          {localized.body}
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
          <time
            className="tabular-nums"
            dateTime={notification.createdAt}
            title={fullDate}
          >
            {formatNotificationTime(notification.createdAt, language)}
          </time>
        </span>
      </span>
    </>
  );

  return (
    <article
      className={`group relative overflow-hidden rounded-lg border transition-colors ${
        isUnread
          ? "border-brand-400/25 bg-brand-500/[0.07]"
          : "border-white/5 bg-slate-950/35 hover:border-slate-700/80"
      }`}
    >
      {isUnread && (
        <span
          aria-hidden="true"
          className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-brand-300 shadow-[0_0_16px_rgba(147,197,253,0.5)]"
        />
      )}
      <div className="flex gap-3 p-3 pl-4">
        {href ? (
          <Link
            to={href}
            onClick={() => {
              if (isUnread) onMarkRead(notification.id);
              onOpenIssue();
            }}
            className="flex min-h-11 min-w-0 flex-1 gap-3 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
          >
            {content}
          </Link>
        ) : (
          <div className="flex min-w-0 flex-1 gap-3">{content}</div>
        )}
        {isUnread && (
          <button
            type="button"
            onClick={() => onMarkRead(notification.id)}
            className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
            aria-label={t("destek_mark_read_aria").replace(
              "{title}",
              localized.title,
            )}
            title={t("destek_mark_read")}
          >
            <Check size={15} />
          </button>
        )}
      </div>
    </article>
  );
}
